import { useState, useEffect } from 'react';
import { api } from '../../api';

interface Props {
  domainId: string;
}

interface BrandVoiceProfile {
  id: string;
  domainId: string;
  tone: string[];
  persona: string | null;
  vocabulary: { preferred: string[]; avoided: string[] };
  writingStyle: string | null;
  createdAt: string;
  updatedAt: string;
}

export function AfiBrandVoicePage({ domainId }: Props) {
  const [profile, setProfile] = useState<BrandVoiceProfile | null | undefined>(undefined);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const [queued, setQueued] = useState(false);

  useEffect(() => {
    api.domains.get(domainId)
      .then((domain) => {
        // Brand voice profile is attached to domain detail if present
        const bv = (domain as unknown as { brandVoiceProfile?: BrandVoiceProfile | null }).brandVoiceProfile;
        setProfile(bv ?? null);
      })
      .catch(() => setProfile(null));
  }, [domainId]);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshError('');
    setQueued(false);
    try {
      await api.domains.refreshBrandVoice(domainId);
      setQueued(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('409') || msg.toLowerCase().includes('in progress')) {
        setRefreshError('Extraction already in progress — check back in a few minutes.');
      } else {
        setRefreshError('Failed to queue brand voice extraction. Please try again.');
      }
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9] p-8">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#1D9E75] mb-1">
          Auto-fix
        </p>
        <h1 className="text-2xl font-semibold text-[#0F172A]">Brand voice</h1>
        <p className="text-sm text-[#475569] mt-1">
          The voice profile used to generate and tone-match content fixes.
        </p>
      </div>

      {profile === undefined ? (
        <div className="bg-white rounded-[6px] p-6 animate-pulse">
          <div className="h-4 w-48 bg-gray-100 rounded mb-3" />
          <div className="h-3 w-64 bg-gray-100 rounded mb-2" />
          <div className="h-3 w-56 bg-gray-100 rounded" />
        </div>
      ) : profile === null ? (
        <div className="bg-white rounded-[6px] p-8">
          <div className="max-w-md">
            <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#94A3B8] mb-2">
              Profile pending
            </p>
            <p className="text-sm text-[#0F172A] font-medium mb-1">
              Brand voice profile not yet extracted
            </p>
            <p className="text-sm text-[#475569] mb-6">
              A brand voice profile is built automatically when your domain is audited. Run a new
              audit to populate it.
            </p>
            {refreshError && (
              <p className="text-sm text-red-500 mb-4">{refreshError}</p>
            )}
            {queued ? (
              <div className="rounded-[6px] border border-green-200 bg-green-50 px-4 py-3">
                <p className="text-sm font-medium text-green-800">Extraction queued</p>
                <p className="text-xs text-green-700 mt-0.5">Brand voice extraction is running in the background. Refresh this page in a few minutes to see the results.</p>
              </div>
            ) : (
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-[#0F172A] text-white text-sm font-medium px-5 py-2.5 rounded-[6px] hover:bg-[#0F6E56] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75]"
              >
                {refreshing ? 'Queuing...' : 'Run brand voice extraction'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Tone */}
          {profile.tone.length > 0 && (
            <div className="bg-white rounded-[6px] p-6">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#94A3B8] mb-3">
                Tone
              </p>
              <div className="flex flex-wrap gap-2">
                {profile.tone.map((t) => (
                  <span
                    key={t}
                    className="text-xs font-medium text-[#0F172A] bg-[#F1F5F9] border border-[#E2E8F0] px-2.5 py-1 rounded capitalize"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Writing style */}
          {profile.writingStyle && (
            <div className="bg-white rounded-[6px] p-6">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#94A3B8] mb-2">
                Writing style
              </p>
              <p className="text-sm text-[#0F172A]">{profile.writingStyle}</p>
            </div>
          )}

          {/* Vocabulary */}
          {(profile.vocabulary.preferred.length > 0 || profile.vocabulary.avoided.length > 0) && (
            <div className="bg-white rounded-[6px] p-6">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#94A3B8] mb-3">
                Vocabulary
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.vocabulary.preferred.length > 0 && (
                  <div>
                    <p className="text-xs text-[#475569] font-medium mb-2">Preferred terms</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.vocabulary.preferred.map((term) => (
                        <span
                          key={term}
                          className="text-xs text-[#1D9E75] bg-green-50 border border-green-200 px-2 py-0.5 rounded"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {profile.vocabulary.avoided.length > 0 && (
                  <div>
                    <p className="text-xs text-[#475569] font-medium mb-2">Avoided terms</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.vocabulary.avoided.map((term) => (
                        <span
                          key={term}
                          className="text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="pt-2">
            <p className="text-xs text-[#94A3B8]">
              Last updated {new Date(profile.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
